import { Grid } from "@chakra-ui/react";
import { GetLayout } from "components/get-layout";
import { Fluxogram } from "components/pipeline-components/fluxogram";
import { PipelineEndpoints } from "components/pipeline-components/utils";
import { PipelineSidePanel } from "components/pipeline-components/side-panel/index";
import { PipelineBlocksPanel } from "components/pipeline-components/blocks-panel";
import { useCallback, useEffect, useReducer, useState } from "react";
import { StatesMachine } from "components/pipeline-components/classes/state-machine/state-machine";
import { StateProps } from "components/pipeline-components/classes/state-machine/state";
import { Toast } from "components/toast";
import { pipelineReducer } from "components/pipeline-components/classes/state-machine/state-control";
import { RoutePermissionFunction, useRouter } from "router-manager/parts";
import { MailingTableRow } from "components/mailing-components/mailing-table";
import { useStompClient } from "hooks/use-stomp-client";
import {
  addOperation,
  canInsertOperation
} from "components/pipeline-components/pipeline-commands";
import { apiHigienizacaoJWT } from "api/api-higienizacao";
import { TutorialPipelineModal } from "components/pipeline-components/tutorial-pipeline";

const url =
  "wss://higienizacao.novapowerhub.com.br/api/higienizacao-pipeline/data-websocket";

const PipelinePage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [pipelineData, dispatchPipelineData] = useReducer(
    pipelineReducer,
    undefined
  );
  const [mailingData, setMailingData] = useState<Partial<MailingTableRow>>({});
  const [isMailingLoading, setIsMailingLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [midiaFileWpp, setMidiaFileWpp] = useState<File | undefined>();
  const [selectedStateId, setSelectedStateId] = useState<number | undefined>();
  const [permitedShowTemplate, setPermitedShowTemplate] = useState(true);
  const router = useRouter();
  const listMailingData = router.getPagePayload();
  const id = router.pathVariable.get("id")!;

  const { connect, subscribe, connected, disconnect } = useStompClient({
    connectHeaders: async () => {
      const validToken = await apiHigienizacaoJWT.getValidAccessToken();
      return { Authorization: `Bearer ${validToken}` };
    },
    url
  });

  const getMailingData = useCallback(async () => {
    setIsMailingLoading(true);
    try {
      const data =
        listMailingData || (await PipelineEndpoints.getMailingData(Number(id)));
      setMailingData(data);
    } catch (err) {
      Toast({
        title: "Erro ao carregar pipeline",
        description: "Mailing não encontrado ou excluido",
        status: "error"
      });
      router.push("/mailing/pipelines-list");
    } finally {
      setIsMailingLoading(false);
    }
  }, [id, listMailingData, router]);

  const GETPipelineData = useCallback(async () => {
    setIsLoading(true);
    try {
      const pipelineData = await PipelineEndpoints.getPipelineData(id);
      dispatchPipelineData({ type: "SET", payload: pipelineData });
      setSelectedStateId(undefined);
      if (!pipelineData.finished) connect();
    } catch (err: any) {
      if (err?.statusCode !== 404) {
        Toast({ title: "Erro ao carregar dados do pipeline", status: "error" });
      }
      dispatchPipelineData({
        type: "SET",
        payload: new StatesMachine({ idMailing: Number(id), states: [] })
      });
      setSelectedStateId(undefined);
    } finally {
      setIsLoading(false);
    }
  }, [connect, id]);

  useEffect(() => {
    GETPipelineData();
    getMailingData();
  }, [GETPipelineData, getMailingData]);

  const updatePipelineData = useCallback(
    (updater: (draft: StatesMachine) => StatesMachine | void) => {
      dispatchPipelineData({ type: "MUTATE", updater });
    },
    [dispatchPipelineData]
  );

  // const isFinished = !!pipelineData?.finished;
  const isPipelineRunning = !!pipelineData?.id && !pipelineData.finished;
  const isPipelineCreated = !!pipelineData?.id;

  const handleInsertOperation = useCallback(
    (operation: StateProps["operation"]) => {
      if (!pipelineData || isPipelineRunning || isPipelineCreated) return;
      if (!canInsertOperation(pipelineData, operation)) return;

      dispatchPipelineData({
        type: "MUTATE",
        updater: (draft) => {
          addOperation(draft, operation);
        }
      });
    },
    [dispatchPipelineData, isPipelineCreated, isPipelineRunning, pipelineData]
  );

  const handleCreatePipeline = useCallback(async () => {
    const errors = pipelineData?.checkStateFields();
    if (errors && errors.length > 0) {
      Toast({
        title: "Erro ao salvar pipeline",
        description: errors.map((e) => e.message).join(", "),
        status: "error"
      });
      return;
    }

    setIsSaving?.(true);
    try {
      const data = await PipelineEndpoints.savePipelineData(
        pipelineData!,
        mailingData,
        midiaFileWpp
      );
      Toast({
        title: "Pipeline salvo com sucesso",
        status: "success"
      });
      dispatchPipelineData({ type: "SET", payload: data });
      setMidiaFileWpp(undefined);
      connect();
    } catch (e) {
      Toast({ title: "Erro ao salvar pipeline", status: "error" });
    } finally {
      setIsSaving?.(false);
    }
  }, [connect, dispatchPipelineData, mailingData, midiaFileWpp, pipelineData]);

  const isEditable = !isPipelineCreated;
  const isTemplateMailingResolved =
    mailingData.convenio !== undefined &&
    mailingData.tipoOperacao !== undefined;
  const isWaitingTemplateDecision =
    !isPipelineCreated &&
    isEditable &&
    permitedShowTemplate &&
    isMailingLoading;
  const isShowPipelineTemplate =
    !isPipelineCreated &&
    isEditable &&
    permitedShowTemplate &&
    isTemplateMailingResolved;
  const isShowBlocksPanel =
    !isShowPipelineTemplate && !isWaitingTemplateDecision;

  return (
    <GetLayout pageContentProps={{ p: 0, css: { scrollbarGutter: "auto" } }}>
      <TutorialPipelineModal />
      <Grid
        h="100%"
        templateColumns={isShowBlocksPanel ? "250px 1fr" : "1fr"}
        pos="relative"
      >
        {isShowBlocksPanel && (
          <PipelineBlocksPanel
            pipelineData={pipelineData}
            isDisabled={isPipelineRunning || isPipelineCreated}
            onInsertOperation={handleInsertOperation}
            mailingData={mailingData}
          />
        )}
        <Fluxogram
          pipelineData={pipelineData}
          isLoading={isLoading}
          subscribe={subscribe}
          dispatchPipelineData={dispatchPipelineData}
          isWsConnected={connected}
          disconnect={disconnect}
          selectedStateId={selectedStateId}
          onSelectState={setSelectedStateId}
          isEditable={isEditable}
          isSaving={isSaving}
          setIsSaving={setIsSaving}
          mailingData={mailingData}
          handleCreatePipeline={handleCreatePipeline}
          isShowPipelineTemplate={isShowPipelineTemplate}
          setPermitedShowTemplate={setPermitedShowTemplate}
        />
        <PipelineSidePanel
          isLoading={isLoading}
          pipelineData={pipelineData}
          mailingData={mailingData}
          updatePipelineData={updatePipelineData}
          selectedStateId={selectedStateId}
          onClearSelection={() => setSelectedStateId(undefined)}
          midiaFileWpp={midiaFileWpp}
          onMidiaFileWppChange={setMidiaFileWpp}
        />
      </Grid>
    </GetLayout>
  );
};

export default PipelinePage;

export const setRoutePermissions: RoutePermissionFunction = (route) => {
  route.setUserTypesPermission([
    "SUPER",
    "REGIONAL",
    "COMERCIAL",
    "PARCEIRO_GERENTE_ESTRATEGICO",
    "PARCEIRO_GERENTE_VAREJO",
    "PARCEIRO_MASTER",
    "ASCOM"
  ]);
  route.setColomboUserTypesPermission([
    "COMERCIAL",
    "PARCEIRO_MASTER",
    "PARCEIRO_VENDEDOR"
  ]);
};

// {
//   HIGIENIZACAO: {
//     REFIN,
//     SAQUE_COMPLEMENTAR,
//     MARGEM_CONVENIO,
//     SIMULACAO_AUMENTO,
//   },

//   SIMULACAO_PORTABILIDADE: {
//     SIMULACAO_PORTABILIDADE,
//   },

//   FILTRO: {
//     idade,
//     banco_operacao,
//     saldo_devedor,
//     taxa_contrato,
//     valor_parcela,
//     valor_liberado,
//     quantidade_contratos,
//     numero_parcelas_pagas,
//     valor_antecipacao,
//     valor_liberado_saque_rcc,
//     valor_liberado_saque_rmc,
//     margem_aumento,
//     contratos_enquadrados,
//     especies,
//     possui_representante_legal,
//     meio_pagamento,
//     valor_beneficio,
//     ddb,
//   },

//   TELEFONIA: {
//     telefoniaApi,
//   },

//   DIGITACAO: {
//     banco,
//     usuarioDigitacao,
//     usuarioDigitacaoSenha,
//     cpfUsuarioCertificado,
//     tabela,
//     tipoColunaMargem,
//     typeCodeC6,
//     novaIsencao,
//   },

//   ENVIA_WHATS: {
//     enviarParaCampanhaWhatsApp,
//   },

//   ENVIA_DISCADORA: {
//     enviarParaDiscadora,
//   },

//   ENVIA_CAMPANHA_SMS: {
//     enviarParaCampanhaSMS,
//   },

//   ENVIA_ATENDIMENTO: {
//     enviaAtendimento,
//   },
// }

// {
//   formatedBody: {
//     mailingId: 183978,
// pipelines: [
//   {
//     stage: "HIGIENIZACAO",
//     configJson: {
//       SIMULACAO_AUMENTO: {
//         coeficiente: 0.05,
//       },
//     },
//     ordem: 1,
//   },
//   {
//     stage: "FILTRO",
//     configJson: {
//       idade: {
//         gte: 10,
//         lte: 20,
//       },
//       banco_operacao: ["BB", "BMG"],
//       saldo_devedor: {
//         gte: 10,
//         lte: 20,
//       },
//       taxa_contrato: {
//         gte: 10,
//         lte: 20,
//       },
//       valor_parcela: {
//         gte: 10,
//         lte: 20,
//       },
//       valor_liberado: {
//         gte: 10,
//         lte: 20,
//       },
//       quantidade_contratos: {
//         gte: 10,
//         lte: 20,
//       },
//       numero_parcelas_pagas: {
//         gte: 10,
//         lte: 20,
//       },
//       margem_aumento: {
//         gte: 10,
//         lte: 20,
//       },
//       especies: [1],
//       possui_representante_legal: true,
//       meio_pagamento: "CONTA_CORRENTE",
//       valor_beneficio: {
//         gte: 10,
//         lte: 20,
//       },
//       ddb: {
//         gte: "01/01/2026",
//         lte: "15/01/2026",
//       },
//     },
//     ordem: 2,
//   },
//   {
//     stage: "TELEFONIA",
//     configJson: {
//       api: "API_TELEFONIA",
//     },
//     ordem: 3,
//   },
//   {
//     stage: "DIGITACAO",
//     configJson: {
//       banco: "C6",
//       usuarioDigitacao: "07908921973_000374",
//       tabela: "220058",
//       usuarioDigitacaoSenha: "Mabe@1531",
//       cpfUsuarioCertificado: "093425",
//       tipoColunaMargem: "MARGEM_AUMENTO",
//       novaIsencao: true,
//     },
//     ordem: 4,
//   },
//   {
//     stage: "ENVIA_WHATS",
//     configJson: {},
//     ordem: 5,
//   },
//   {
//     stage: "ENVIA_CAMPANHA_SMS",
//     configJson: {},
//     ordem: 6,
//   },
//   {
//     stage: "ENVIA_ATENDIMENTO",
//     configJson: {},
//     ordem: 7,
//   },
// ],
//   },
//   errors: {},
//   type: "SUCCESS",
// };
